import React, { useState, useEffect } from "react";
import { MoreHorizontal, Trash2, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InlineEditableInput } from "./InlineEditableInput";
import { Button } from "../ui/button";
import useLayoutStore from "@/store/zustand/layoutStore";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { STATUS_TYPE_DISPLAY_NAMES } from "./config/statusConfig";
import { STATUS_CONSTANTS, IconType } from "./constants";
import { StatusInterface } from "./StatusesContent";
import { useSearchParams } from "react-router";

interface StatusGroupItemProps {
  status: StatusInterface;
  getStatusIcon: (status: string, iconType?: IconType) => React.ReactNode;
  onUpdate: (newName: string) => Promise<void>;
  onDelete: () => void;
}

export const StatusGroupItem: React.FC<StatusGroupItemProps> = React.memo(
  ({ status, getStatusIcon, onUpdate, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [optimisticName, setOptimisticName] = useState<string | null>(null);
    const [resetKey, setResetKey] = useState(0);

    // Only reset optimistic name if it's set and status name changed from server
    // This prevents unnecessary re-renders when status.name hasn't actually changed
    useEffect(() => {
      if (optimisticName !== null && optimisticName !== status.name) {
        setOptimisticName(null);
      }
    }, [status.name, optimisticName]);

    const setScrollToStatus = useLayoutStore(
      (state) => state.setScrollToStatus,
    );
    const [, setSearchParams] = useSearchParams();

    const canvasCount = status.count;

    const handleSave = async (newName: string) => {
      const trimmedName = newName.trim();

      if (trimmedName.length > STATUS_CONSTANTS.MAX_STATUS_NAME_LENGTH) {
        toast.error(
          `Status name must be ${STATUS_CONSTANTS.MAX_STATUS_NAME_LENGTH} characters or less`,
        );
        setOptimisticName(null);
        setResetKey((prev) => prev + 1);
        return;
      }

      // Only update if name actually changed
      if (trimmedName !== status.name.trim()) {
        try {
          setOptimisticName(trimmedName);

          await onUpdate(trimmedName);

          setOptimisticName(null);
          setIsEditing(false);
        } catch (error) {
          setOptimisticName(null);
          const errorMessage = error.message || "Unknown error";
          if (
            errorMessage.includes("too long") ||
            errorMessage.includes("length")
          ) {
            toast.error("Status name is too long. Please use a shorter name.");
          } else {
            toast.error(`Failed to update status name: ${errorMessage}`);
          }
          setResetKey((prev) => prev + 1);
        }
      } else {
        setIsEditing(false);
      }
    };

    const handleCancel = () => {
      setOptimisticName(null);
      setIsEditing(false);
    };

    const handleNavigateToCanvas = () => {
      setScrollToStatus(status.name);
      // Navigate to Projects/Board view by removing nav param
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("nav");
        return newParams;
      });
    };

    const handleDelete = () => {
      if (canvasCount > 0) {
        toast.error(
          `Cannot delete status "${
            status.name
          }" as it is in use by ${canvasCount} canvas${
            canvasCount === 1 ? "" : "es"
          }.`,
        );
        return;
      }
      onDelete();
    };

    const handleEdit = () => {
      setIsDropdownOpen(false);
      setOptimisticName(null);
      setIsEditing(true);
    };

    // Determine if this is a default status that should not be deletable
    // Default statuses can only be deleted if they have custom column configs
    const isDefaultStatus = status.isDefault ?? false;

    return (
      <div className="w-full">
        <div
          className={cn(
            "flex items-start justify-between rounded-md group py-2 pr-1",
            optimisticName && "bg-secondary-n-950/50",
          )}
        >
          <div className="flex items-start gap-1 flex-1">
            {!isEditing && (
              <div className="flex items-center justify-center w-[26px] h-[26px] rounded-md bg-secondary-n-950">
                {getStatusIcon(status.name, IconType.FILTER_ICON)}
              </div>
            )}

            <div className="flex-1 flex flex-col items-start h-[26px] justify-center min-w-0">
              <div className="flex w-full items-center gap-1">
                <InlineEditableInput
                  key={`${status.name}-${resetKey}`}
                  value={optimisticName || status.name}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  placeholder="Status name"
                  className="flex-1 ml-0"
                  showEditIcon={false}
                  isEditing={isEditing}
                  onEditStateChange={(editing) => setIsEditing(editing)}
                />
              </div>
              {!isEditing && canvasCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={handleNavigateToCanvas}
                  className="text-2xs p-0 leading-3 text-secondary-n-400 hover:bg-transparent block hover:text-secondary-n-50 h-auto"
                >
                  {canvasCount} canvas{canvasCount === 1 ? "" : "es"}
                </Button>
              )}
            </div>
          </div>

          {!isEditing && (
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <DropdownMenuTrigger asChild className="pr-1">
                <Button
                  variant="ghost"
                  className="p-0 w-6 h-6 opacity-100 text-secondary-n-600"
                  title="More options"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuItem
                  onClick={handleEdit}
                  className="text-secondary-n-50 hover:text-secondary-n-300 cursor-pointer"
                  title="Edit status name"
                >
                  <Pencil className="w-3 h-3 mr-2" />
                  Edit
                </DropdownMenuItem>
                {!isDefaultStatus && (
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-red-400 hover:text-red-300 cursor-pointer"
                    title={
                      canvasCount > 0
                        ? `Cannot delete: ${canvasCount} canvas${
                            canvasCount === 1 ? "" : "es"
                          } using this status`
                        : "Delete this status"
                    }
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    );
  },
);

StatusGroupItem.displayName = "StatusGroupItem";
